'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { ProductStock } from '@eliadmin/core/src/components/product/product-stock';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent, CardFooter } from '@eliadmin/core/src/components/ui/card';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
} from '@eliadmin/core/src/utils/product-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { Fragment } from 'react';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';
import { AskForPriceDialog } from '@eliadmin/core/src/components/product/dialog/ask-for-price-dialog';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { useState } from 'react';

export type ProductCardProps = {
  product: Product;
  className?: {
    main?: string;
  };
};

function ProductCardInternal({ product, className }: ProductCardProps) {
  //   const isAuth = useGlobalStore(state => state.config.isAuth);
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  // const api = useGlobalStore(state => state.config.api);
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, selectedValues);
  const priceDisplay = getProductPriceDisplay(product);
  const ASK_FOR_PRICE = priceDisplay.priceOnRequest;
  const [isOpenAskForPriceDialog, setIsOpenAskForPriceDialog] = useState(false);

  return (
    <Card
      className={cn(
        // shadow-[0_-2px_60px_-15px_rgba(0,0,0,0.15)] removed, kept it there because of nice regex pattern
        'relative flex h-full min-w-[100px]  flex-col gap-2 border border-border',
        'ea-product-card',
        className?.main,
        '@container',
      )}
    >
      {/* {!!templateSettings.product_teaser?.favorite_icon?.enabled && (
        <div className={cn('absolute right-4 top-4 z-10', 'ea-product-card__add-to-favorites')}>
          <AddToFavoritesButton slug={product.slug} isFavorite={product.is_favorite ?? false} />
        </div>
      )} */}

      <div
        className={cn(
          'absolute right-4 top-4 z-10 flex flex-col flex-wrap items-center gap-1 text-xs',
          'ea-product-card__badges',
        )}
      >
        {product.tags?.map((badge, i) => (
          <Fragment key={`badge-${i}`}>
            <Badge
              style={{ backgroundColor: badge.color, color: getContrastYIQ(badge.color) }}
              size="sm"
              className={cn('ea-product-card__badge')}
            >
              {badge.name}
            </Badge>
          </Fragment>
        ))}
      </div>

      <CardContent
        className={cn(
          'flex flex-1 flex-col gap-2 p-1 pb-0 @[200px]:p-3',
          'ea-product-card__content',
        )}
      >
        {image?.url && (
          <a href={productUrl} aria-label={product.name} className="">
            <Image
              alt={resolveImageAlt(image, product?.name)}
              src={image.url}
              width="0"
              height="0"
              sizes="100vw"
              className="aspect-square w-full rounded-lg object-contain"
            />
          </a>
        )}

        <a
          href={productUrl}
          className={cn(
            'line-clamp-3 text-ellipsis text-center text-sm font-bold @[200px]:text-lg',
            'ea-product-card__name',
          )}
        >
          {product.name}
        </a>

        {!!templateSettings.product_teaser?.short_description?.enabled && (
          <HtmlRendererRaw
            className={cn(
              'line-clamp-4  overflow-hidden text-ellipsis text-center text-sm font-normal text-muted-foreground',
              'ea-product-card__short-description',
            )}
            html={product.short_description}
          />
        )}
      </CardContent>

      {/* footer */}
      <CardFooter
        className={cn(
          'flex flex-col justify-end gap-2 p-1 pt-0 @[200px]:p-3',
          'ea-product-card__footer',
        )}
      >
        <div
          className={cn(
            'flex w-full flex-row items-center justify-between',
            'ea-product-card__footer-top',
          )}
        >
          {!!templateSettings.product_teaser?.availability?.enabled && (
            <ProductStock
              className={cn(
                'absolute left-3',
                'ea-product-cart__stock',
                priceDisplay.showPreviousPrice ? 'top-10' : 'top-4',
              )}
            />
          )}

          {/* {product.video && (
            <Button
              variant="link"
              className={cn(
                'flex h-auto flex-row items-center space-x-2 px-0 py-0 text-destructive underline',
                'ea-product-card__video-button',
              )}
              // onClick={() => handleVideoClick(product.video ?? '')}
            >
              <FaYoutube className={cn('h-4 w-4', 'ea-product-card__video-icon')} />
              <span
                className={cn(
                  'text-xs font-medium text-destructive',
                  'ea-product-card__video-text',
                )}
              >
                Video
              </span>
            </Button>
          )} */}
        </div>

        {/* Price */}
        {/* <Price product={product} filterVariants={filterVariants} /> */}
        {!ASK_FOR_PRICE ? (
          <ProductPrice
            product={product}
            className={{
              main: cn('items-center', 'ea-product-card__product-price'),
              discountWrapper: 'absolute left-3 top-4',
              priceAfterDiscount: 'text-[18px]',
            }}
          />
        ) : (
          <div className={cn('ea-product-card__ask-for-price')}>
            <button
              type="button"
              className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
              onClick={() => setIsOpenAskForPriceDialog(true)}
            >
              {t('text2278')}
            </button>
            <AskForPriceDialog
              isOpen={isOpenAskForPriceDialog}
              setIsOpen={setIsOpenAskForPriceDialog}
              product={product}
            />
          </div>
        )}

        <div className="flex w-full gap-2">
          {!!templateSettings.product_teaser?.cart_button?.enabled && !ASK_FOR_PRICE && (
            <AddToCartButton
              product={product}
              showIcon={!!templateSettings.product_teaser?.cart_icon}
              /** TODO: sellable */
              sellable={true}
              className={cn('flex-grow', 'ea-product-card__add-to-cart-button')}
              // variant="accent"
            />
          )}
          {!!templateSettings.product_teaser?.favorite_icon?.enabled && (
            <div className={cn('hidden @[200px]:block', 'ea-product-card__add-to-favorites')}>
              {/* <AddToFavoritesButton slug={product.slug} isFavorite={product.is_favorite ?? false} /> */}
            </div>
          )}
        </div>

        {!!templateSettings.product_teaser?.detail_button?.enabled && (
          <Button
            asChild
            variant="secondary"
            className={cn('w-full', 'ea-product-card__detail-button')}
          >
            <a href={productUrl} aria-label={`${t('text533')}: ${product.name}`}>{t('text533')}</a>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <VariantsProvider state={{ product: props.product }}>
      <DialogProvider>
        <ProductCardInternal {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
}
